import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import BrandSelect from '../ui/BrandSelect';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Actions as CONN_ACTIONS } from '../../store/actions/connectionActions';
import { Actions as IMPORT_ACTIONS } from '../../store/actions/intelligentImportActions';
import ACTIONS from '../../store/action';
import { mappingRowSelect } from '../utils/ReactSelectStyles';
import {
  INTERACTIVE_STATUSES,
  PIPELINE_STATUSES,
  TERMINAL_STATUSES,
} from '../../constants/importConstants';
import { importNoDataMessage, isImportNoData } from './importNoData';

/**
 * Which sync body shapes the descriptor allows.
 * bodySchema.oneOf may list 'year' and/or 'from+to' (or JSON-schema required arrays).
 * @param {object} [bodySchema]
 * @returns {{ year: boolean, range: boolean }}
 */
const resolveSyncModes = (bodySchema) => {
  const oneOf = bodySchema?.oneOf;
  if (!Array.isArray(oneOf) || !oneOf.length) {
    return { year: true, range: true };
  }

  let year = false;
  let range = false;
  oneOf.forEach((item) => {
    if (item === 'year') year = true;
    if (item === 'from+to' || item === 'range') range = true;
    const required = item?.required;
    if (Array.isArray(required)) {
      if (required.includes('year')) year = true;
      if (required.includes('from') && required.includes('to')) range = true;
    }
  });

  if (!year && !range) return { year: true, range: true };
  return { year, range };
};

const MODE_OPTIONS = [
  { value: 'year', label: 'Reporting year' },
  { value: 'range', label: 'Date range' },
];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Poll staging job until empty, ready for review, or hard failure — without leaving Connections.
 * @param {string} importJobId
 * @param {(id: string) => Promise<object|null>} getImportStatus
 * @returns {Promise<{ kind: 'no_data'|'ready'|'failed'|'timeout', job?: object }>}
 */
const waitForImportOutcome = async (importJobId, getImportStatus) => {
  let interval = 1200;
  const maxAttempts = 45;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const job = await getImportStatus(importJobId);
    if (job) {
      const workerStatus = job.functionStatus;
      if (workerStatus?.status === 'FAILED') {
        return {
          kind: 'failed',
          job: {
            ...job,
            lastError: job.lastError || workerStatus.errorMessage || workerStatus.message,
          },
        };
      }
      if (isImportNoData(job)) {
        return { kind: 'no_data', job };
      }

      const status = job.pipelineStatus || job.status;
      if (INTERACTIVE_STATUSES.includes(status)) {
        return { kind: 'ready', job };
      }
      if (
        status === PIPELINE_STATUSES.COMPLETE ||
        status === PIPELINE_STATUSES.PARTIAL_COMPLETE
      ) {
        return { kind: 'ready', job };
      }
      if (status === PIPELINE_STATUSES.FAILED || status === PIPELINE_STATUSES.CANCELLED) {
        return { kind: 'failed', job };
      }
      if (TERMINAL_STATUSES.includes(status) && !isImportNoData(job)) {
        return { kind: 'failed', job };
      }
    }

    await sleep(interval);
    interval = Math.min(Math.round(interval * 1.35), 6000);
  }

  return { kind: 'timeout' };
};

const SpendImportPanel = ({
  connectorId,
  descriptor,
  currentOrg,
  loading,
  syncConnection,
  getConnectionDescriptor,
  getImportStatus,
  startImportPolling,
  showAlert,
}) => {
  const history = useHistory();
  const syncModes = useMemo(() => resolveSyncModes(descriptor?.ingest?.bodySchema), [descriptor]);
  const modeOptions = useMemo(
    () => MODE_OPTIONS.filter((opt) => (opt.value === 'year' ? syncModes.year : syncModes.range)),
    [syncModes],
  );

  const [mode, setMode] = useState('year');
  const [selectedYear, setSelectedYear] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [localError, setLocalError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [preparing, setPreparing] = useState(false);

  useEffect(() => {
    if (!modeOptions.some((o) => o.value === mode) && modeOptions[0]) {
      setMode(modeOptions[0].value);
    }
  }, [mode, modeOptions]);

  const yearOptions = useMemo(() => {
    const { reportingYearEndDay, reportingYearEndMonth } = currentOrg || {};
    const years = [...new Array(new Date().getFullYear() - 2017)].map((_, index) => 2018 + index);
    if (reportingYearEndMonth && (String(reportingYearEndMonth) !== '12' || String(reportingYearEndDay) !== '31')) {
      years.push(years[years.length - 1] + 1);
    }
    return years.map((year) => ({ value: year, label: String(year) }));
  }, [currentOrg]);

  const selectedMode = modeOptions.find((o) => o.value === mode) || modeOptions[0] || null;
  const activeMode = selectedMode?.value || mode;

  const canImport =
    descriptor?.status === 'connected' &&
    (!Array.isArray(descriptor?.capabilities) || descriptor.capabilities.includes('ingest.spend'));

  const rangeValid = Boolean(from && to && from <= to);
  const canSubmit = activeMode === 'year' ? Boolean(selectedYear?.value) : rangeValid;
  const busy = Boolean(loading?.sync || preparing);

  const notifyNoData = (message) => {
    const text = message || importNoDataMessage();
    setInfoMessage(text);
    showAlert(true, text, 'warning', 6000);
  };

  const onImport = async () => {
    if (!canSubmit || busy) return;
    setLocalError(null);
    setInfoMessage(null);
    if (activeMode === 'range' && from > to) {
      setLocalError('From date must be on or before To date.');
      return;
    }

    const window =
      activeMode === 'range'
        ? { from, to }
        : { year: selectedYear.value };

    setPreparing(true);
    try {
      const result = await syncConnection(connectorId, window, descriptor?.ingest);
      if (isImportNoData(result)) {
        notifyNoData(importNoDataMessage(result));
        return;
      }

      const importJobId =
        result?.import_job_id || result?.importJobId || result?.data?.import_job_id || result?.data?.importJobId;
      const path = result?.path || result?.data?.path;

      if (!(path === 'staging' && importJobId)) {
        setLocalError('Unexpected sync response. Expected staging import job.');
        return;
      }

      const pipelineAsync = result?.pipeline_async ?? result?.pipelineAsync ?? result?.data?.pipeline_async;
      if (pipelineAsync !== false) {
        showAlert(
          true,
          'Import started in the background. You can leave the page and return from Usage Data when it is ready.',
          'success',
          8000,
        );
        startImportPolling();
        history.push(`/intelligent-import/${importJobId}`);
        return;
      }

      // Stay on this panel until we know the window has rows — keep period fields intact.
      const outcome = await waitForImportOutcome(importJobId, getImportStatus);
      if (outcome.kind === 'no_data') {
        notifyNoData(importNoDataMessage(outcome.job));
        return;
      }
      if (outcome.kind === 'ready') {
        history.push(`/intelligent-import/${importJobId}`);
        return;
      }
      if (outcome.kind === 'failed') {
        setLocalError(
          outcome.job?.lastError ||
            outcome.job?.last_error ||
            outcome.job?.message ||
            'Import failed',
        );
        return;
      }
      setLocalError('Import is taking longer than expected. Check Imports for progress.');
    } catch (err) {
      if (err?.code === 'NEEDS_EXTERNAL_ORGANISATION' || err?.status === 409) {
        setLocalError(err.message || 'Select an organisation before importing.');
        getConnectionDescriptor(connectorId);
        return;
      }
      setLocalError(err.message || 'Import failed');
    } finally {
      setPreparing(false);
    }
  };

  if (descriptor?.status === 'needs_organisation') {
    return (
      <Card className='bg-card border-border'>
        <CardHeader>
          <CardTitle className='text-base'>Import spend</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-muted-foreground'>Finish organisation selection above before importing spend data.</p>
        </CardContent>
      </Card>
    );
  }

  if (!canImport) {
    return null;
  }

  return (
    <Card className='bg-card text-card-foreground border-border'>
      <CardHeader>
        <CardTitle className='text-base'>Import spend</CardTitle>
      </CardHeader>
      <CardContent className='space-y-3'>
        <p className='text-sm text-muted-foreground'>
          Import purchase invoices and bank spend for a reporting year or custom date range into intelligent-import
          staging for review.
        </p>
        <div className='flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end'>
          {modeOptions.length > 1 && (
            <div className='w-full max-w-xs min-w-0'>
              <label className='mb-1 block text-sm font-medium'>Period</label>
              <BrandSelect
                styles={mappingRowSelect}
                options={modeOptions}
                value={selectedMode}
                isDisabled={busy}
                onChange={(opt) => {
                  setMode(opt.value);
                  setLocalError(null);
                  setInfoMessage(null);
                }}
                placeholder='Select period type'
              />
            </div>
          )}

          {activeMode === 'year' && (
            <div className='w-full max-w-xs min-w-0'>
              <label className='mb-1 block text-sm font-medium'>Reporting year</label>
              <BrandSelect
                styles={mappingRowSelect}
                options={yearOptions}
                value={selectedYear}
                isDisabled={busy}
                onChange={setSelectedYear}
                placeholder='Select year'
              />
            </div>
          )}

          {activeMode === 'range' && (
            <>
              <div className='min-w-0'>
                <label htmlFor='spend-import-from' className='mb-1 block text-sm font-medium'>
                  From
                </label>
                <Input
                  id='spend-import-from'
                  type='date'
                  value={from}
                  disabled={busy}
                  onChange={(e) => setFrom(e.target.value)}
                  className='w-full max-w-[11rem] bg-input'
                />
              </div>
              <div className='min-w-0'>
                <label htmlFor='spend-import-to' className='mb-1 block text-sm font-medium'>
                  To
                </label>
                <Input
                  id='spend-import-to'
                  type='date'
                  value={to}
                  disabled={busy}
                  onChange={(e) => setTo(e.target.value)}
                  className='w-full max-w-[11rem] bg-input'
                />
              </div>
            </>
          )}

          <Button loading={busy} disabled={!canSubmit || busy} onClick={onImport}>
            {preparing ? 'Checking for spend…' : 'Import spend data'}
          </Button>
        </div>
        {preparing && (
          <p className='text-sm text-muted-foreground'>Starting your import. Matching continues in the background and may take several minutes.</p>
        )}
        {!preparing && (
          <p className='text-xs text-muted-foreground'>
            Large imports may take several minutes. You can leave after the import starts and track it from Usage Data.
          </p>
        )}
        {infoMessage && (
          <Alert variant='warning'>
            <AlertTriangle size={16} strokeWidth={1.5} />
            <div>
              <AlertTitle>No spend data</AlertTitle>
              <AlertDescription>{infoMessage}</AlertDescription>
            </div>
          </Alert>
        )}
        {localError && (
          <Alert variant='destructive'>
            <AlertTriangle size={16} strokeWidth={1.5} />
            <div>
              <AlertTitle>Import issue</AlertTitle>
              <AlertDescription>{localError}</AlertDescription>
            </div>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  loading: state.connections?.loading,
  currentOrg: state.organisation?.details,
});

const mapDispatchToProps = (dispatch) => ({
  syncConnection: (id, window, ingest) => dispatch(CONN_ACTIONS.syncConnection(id, window, ingest)),
  getConnectionDescriptor: (id) => dispatch(CONN_ACTIONS.getConnectionDescriptor(id)),
  getImportStatus: (id) => dispatch(IMPORT_ACTIONS.getImportStatus(id)),
  startImportPolling: () => dispatch(IMPORT_ACTIONS.setPolling(true)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(ACTIONS.toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SpendImportPanel);
